import React, { useEffect, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import jsQR from 'jsqr';

interface QRScannerProps {
  onScanSuccess: (result: string) => void;
  onScanError: (error: string) => void;
  loading?: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScanSuccess, onScanError, loading = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setIsScanning(true);
          startScanning();
        };
      }
    } catch (err) {
      const errorMessage = 'Không thể truy cập camera. Vui lòng cho phép truy cập camera và thử lại.';
      setError(errorMessage);
      onScanError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsScanning(false);
  };

  const startScanning = () => {
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        scanQRCode();
      }, 500); // Quét mỗi 500ms
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || loading) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const qrResult = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (qrResult && qrResult.data) {
        setIsScanning(false);
        stopCamera();
        onScanSuccess(qrResult.data);
      }
    } catch (err) {
      // Tiếp tục quét
    }
  };

  if (error) {
    return (
      <Alert
        message="Lỗi Camera"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ position: 'relative', textAlign: 'center' }}>
      <div style={{ 
        position: 'relative', 
        display: 'inline-block',
        border: '3px solid #1890ff',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <video
          ref={videoRef}
          style={{
            width: '100%',
            maxWidth: '400px',
            height: 'auto',
            display: 'block'
          }}
          autoPlay
          playsInline
          muted
        />
        
        {/* Overlay với khung quét */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '200px',
          border: '2px solid #52c41a',
          borderRadius: '8px',
          pointerEvents: 'none'
        }}>
          {/* Góc khung quét */}
          <div style={{
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            width: '20px',
            height: '20px',
            borderTop: '4px solid #52c41a',
            borderLeft: '4px solid #52c41a'
          }} />
          <div style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            width: '20px',
            height: '20px',
            borderTop: '4px solid #52c41a',
            borderRight: '4px solid #52c41a'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-2px',
            left: '-2px',
            width: '20px',
            height: '20px',
            borderBottom: '4px solid #52c41a',
            borderLeft: '4px solid #52c41a'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            width: '20px',
            height: '20px',
            borderBottom: '4px solid #52c41a',
            borderRight: '4px solid #52c41a'
          }} />
        </div>

        {loading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '20px',
            borderRadius: '8px',
            color: 'white'
          }}>
            <Spin size="large" />
            <div style={{ marginTop: '10px' }}>Đang xử lý...</div>
          </div>
        )}
      </div>
      
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
      
      <div style={{ marginTop: '10px', color: '#666' }}>
        {isScanning && !loading ? 'Đang quét... Hãy hướng camera vào mã QR' : 'Chuẩn bị camera...'}
      </div>
    </div>
  );
};

export default QRScanner;
